import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { makeStyles, Typography, Paper, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton } from '@material-ui/core';
import { Delete as DeleteIcon, CloudUpload as CloudUploadIcon } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dropzone: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  icon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  fileList: {
    width: '100%',
  },
  fileItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  fileName: {
    wordBreak: 'break-all',
  },
}));

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxSize?: number; // in bytes
  acceptedFileTypes?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  maxFiles = 10,
  maxSize = 100 * 1024 * 1024, // 100MB default
  acceptedFileTypes,
}) => {
  const classes = useStyles();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const errors = rejectedFiles.map((file) => {
          if (file.errors[0].code === 'file-too-large') {
            return `${file.file.name} is too large. Max size is ${maxSize / (1024 * 1024)}MB.`;
          }
          if (file.errors[0].code === 'file-invalid-type') {
            return `${file.file.name} has an invalid file type.`;
          }
          return `${file.file.name} could not be uploaded.`;
        });
        setError(errors.join(' '));
        return;
      }

      // Check if adding these files would exceed the max files limit
      if (files.length + acceptedFiles.length > maxFiles) {
        setError(`You can only upload a maximum of ${maxFiles} files.`);
        return;
      }

      setError(null);
      const newFiles = [...files, ...acceptedFiles];
      setFiles(newFiles);
      onFilesChange(newFiles);
    },
    [files, maxFiles, maxSize, onFilesChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize,
    accept: acceptedFileTypes ? { 'application/octet-stream': [acceptedFileTypes] } : undefined,
  });

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    onFilesChange(newFiles);
  };

  return (
    <Paper className={classes.root}>
      <div {...getRootProps({ className: classes.dropzone })}>
        <input {...getInputProps()} />
        <CloudUploadIcon className={classes.icon} />
        {isDragActive ? (
          <Typography variant="h6">Drop the files here...</Typography>
        ) : (
          <Typography variant="h6">
            Drag and drop files here, or click to select files
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary">
          Maximum {maxFiles} files, up to {maxSize / (1024 * 1024)}MB each
        </Typography>
      </div>

      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error}
        </Typography>
      )}

      {files.length > 0 && (
        <List className={classes.fileList}>
          {files.map((file, index) => (
            <ListItem key={index} className={classes.fileItem}>
              <ListItemText
                primary={
                  <Typography variant="body1" className={classes.fileName}>
                    {file.name}
                  </Typography>
                }
                secondary={`${(file.size / 1024).toFixed(2)} KB`}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" aria-label="delete" onClick={() => removeFile(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default FileUpload;
